import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { useVisibilityPause } from "@/lib/useVisibilityPause";

/**
 * Marketing page cinematic character cameos.
 *
 * Drops short AI-generated character clips (Santa's sleigh flying
 * across the hero, an elf hanging upside down with a lantern,
 * Coalie peeking from a card corner) over the long marketing page
 * so the page reads as a high-end cinematic Christmas experience
 * instead of a flat landing page.
 *
 * How the integration works:
 * - Each clip was generated against a PURE BLACK background so we
 *   can use `mix-blend-mode: screen` to drop the black and let
 *   only the lit character appear over the dark pine page.
 * - Each cameo anchors to a section by DOM id (offsetTop +
 *   per-cameo offset) and recomputes on resize so layout changes
 *   don't strand a clip in the wrong spot.
 * - IntersectionObserver pauses the clip when scrolled off-screen,
 *   the document-hidden hook pauses on tab switch, and the whole
 *   overlay is suppressed for `prefers-reduced-motion` users.
 * - Decorative only: pointer-events-none, aria-hidden.
 */

interface CameoSpec {
  id: string;
  src: string;
  /** Section DOM id this cameo anchors to. */
  anchor: string;
  /** Vertical offset (px) added to the anchor's offsetTop. */
  offsetPx: number;
  /** Horizontal placement. */
  side: "left" | "right" | "full";
  /** Height as vh; width derives from aspect or full-width. */
  heightVh: number;
  /** Aspect ratio used when side != "full". */
  aspect?: "9/16" | "16/9";
  /** Render on mobile? Heavy/large cameos are desktop-only. */
  mobile: boolean;
  /** Optional mobile-specific height override (vh). Smaller so the
   *  cameo doesn't crowd the headline / phone mock on narrow screens. */
  mobileHeightVh?: number;
  /** Optional mobile-specific vertical offset (px). Lets us reposition
   *  the cameo so it tucks into safe whitespace instead of the
   *  headline area on mobile. */
  mobileOffsetPx?: number;
  /** Render in front of (z=20) or behind (z=5) page content. */
  layer: "front" | "back";
  /**
   * Optional extra classNames merged onto the wrapper. Useful for
   * per-cameo responsive overrides (e.g. hiding below a breakpoint
   * where there is no real gutter, or scaling height/left at wider
   * viewports). `!` prefix lets these override the base inline
   * style values for `height` and the `left/right` rule.
   */
  extraClassName?: string;
}

const CAMEOS: CameoSpec[] = [
  // (Removed: the Santa's-sleigh hero sweep cameo — per user
  // feedback the sleigh box was visually too prominent across the
  // top of the hero, conflicting with the headline + phone read.
  // Shooting-star streaks in the side gutters carry the "magical
  // sky" beat now; see ShootingStars in MarketingPage.tsx.)
  // (Removed: the upside-down hanging elf video cameo at the
  // How-It-Works section — per user feedback the inverted pose
  // read as "broken" rather than playful, especially next to the
  // clean upright static lantern elf in the value strip. The
  // static elf-peek-right decoration already covers this section.)
  {
    // Coalie peeks from the right gutter near the mission preview
    // section — "Coalie's up to something" energy.
    id: "coalie-peek",
    src: "/cameos/coalie-peek-corner.mp4",
    anchor: "mission-preview",
    offsetPx: 80,
    side: "right",
    heightVh: 28,
    aspect: "9/16",
    mobile: true,
    layer: "front",
  },
  {
    // Santa peeks from the left gutter in the hero — he's the host
    // and should greet visitors immediately with a warm "the host has
    // arrived" moment, filling the empty left-side space next to the
    // headline.
    //
    // Sizing notes (after the Task #344 measurement pass — the new
    // verifyMarketingCameoOverlap.ts probe drives this; supersedes
    // the earlier lg/xl visual pass that thought a right-edge mask +
    // mix-blend-mode would hide Santa's silhouette inside the H1
    // column — the strict bounding-box check showed it still
    // intersects):
    // - The hero grid container is `mx-auto max-w-7xl` (1280px wide,
    //   centred). At 1280 viewport there is ZERO outside gutter, so
    //   any visible left-anchored cameo with non-trivial width
    //   collides into the H1. The earlier "compact at xl" sizing
    //   (`xl:!h-[24vh]` → ~122px wide at our test viewport) sat right
    //   on top of the headline — the verifier flagged this at 1280.
    // - At 2xl (1536) the gutter opens to (1536-1280)/2 = 128px on
    //   each side. With `2xl:!left-6` and `2xl:!h-[18vh]` (≈91px wide
    //   at the verifier's 900px-tall viewport), the cameo's right edge
    //   stays comfortably inside the gutter and clear of the H1
    //   column.
    // - Below 2xl Santa stays hidden — the shooting-star streaks in
    //   the side gutters (see ShootingStars in MarketingPage.tsx)
    //   carry the magical-sky beat at narrower widths.
    // - `offsetPx` was 60 which left him hugging the top of the
    //   hero. Bumping to 140 vertically centres him against the
    //   ~lg:pt-24 / lg:pb-20 hero padding plus headline + subhead +
    //   CTA stack so he reads as standing alongside the copy
    //   rather than peering down at it.
    // - The `!`-prefixed arbitrary classes intentionally override
    //   the base inline `height` and the side rule from the
    //   wrapper's `style` so we get a true breakpoint-aware sweep
    //   without rewriting the positioning code.
    id: "santa-peek",
    src: "/cameos/santa-peek-wave.mp4",
    anchor: "hero",
    offsetPx: 140,
    side: "left",
    heightVh: 22,
    aspect: "9/16",
    // Mobile cameo intentionally DISABLED. Verified Task #342 against
    // 360 / 375 / 390 / 414 / 430 phone widths with Playwright DOM
    // measurements:
    //   - Hero section uses `px-5` (20px), so the headline H1, the
    //     eyebrow row, and the badge ALL start at x=20 — there is no
    //     real left gutter on a phone.
    //   - Any left-anchored cameo with non-trivial width overlaps
    //     the content column on phones, so Santa stays hidden below
    //     2xl. The shooting-star streaks (see ShootingStars) carry
    //     the "magical sky" beat at narrower widths.
    // Desktop sizing is intentionally conservative — see the longer
    // note above (Task #344) for why we hide Santa below 2xl and use
    // a small `2xl:!h-[18vh]` instead of the previous `xl:!h-[24vh]
    // / 2xl:!h-[30vh]` pair, which the verifier showed overlapped
    // the H1 column.
    mobile: false,
    layer: "front",
    extraClassName: "hidden 2xl:block 2xl:!h-[18vh] 2xl:!left-6",
  },
];

function CameoVideo({ spec }: { spec: CameoSpec }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);
  const [topPx, setTopPx] = useState<number | null>(null);
  const docHidden = useVisibilityPause();
  const isMobile = useIsMobile();
  // Mobile-aware sizing/offset so cameos that opt in via `mobile:true`
  // can shrink and reposition out of the headline / phone-mock zone
  // on narrow screens without affecting their desktop layout.
  const effectiveOffsetPx =
    isMobile && spec.mobileOffsetPx !== undefined
      ? spec.mobileOffsetPx
      : spec.offsetPx;
  const effectiveHeightVh =
    isMobile && spec.mobileHeightVh !== undefined
      ? spec.mobileHeightVh
      : spec.heightVh;

  // Anchor to the target section's position relative to the
  // MarketingPage root. Recompute on resize and whenever the page
  // DOM mutates (so lazy-loaded sections like MissionPreview
  // appearing after Suspense resolves immediately move their
  // cameo into place).
  useLayoutEffect(() => {
    let raf = 0;
    const root = document.getElementById("marketing-cameo-root");
    if (!root) return;
    const compute = () => {
      const target = document.getElementById(spec.anchor);
      if (!target) return;
      // Use bounding rects so we don't have to trust the
      // offsetParent chain — works even if intermediate ancestors
      // change their `position` rules. Both rects are in viewport
      // coords; subtract to get target-relative-to-root.
      const targetRect = target.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      const top = targetRect.top - rootRect.top + effectiveOffsetPx;
      setTopPx(top);
    };
    compute();
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener("resize", onResize);
    // Recompute whenever new sections mount (Suspense fallbacks
    // resolving, lazy chunks landing, etc). One MutationObserver
    // per cameo is cheap; subtree:true scopes to the marketing root.
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    });
    mo.observe(root, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("resize", onResize);
      mo.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [spec.anchor, effectiveOffsetPx]);

  // Pause when off-screen to save battery / decode work.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setVisible(e.isIntersecting);
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Drive playback off visibility + tab-hidden.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!visible || docHidden) {
      v.pause();
    } else {
      // Catch autoplay rejection silently — muted+playsinline should
      // satisfy every browser, but iOS occasionally still throws.
      void v.play().catch(() => {});
    }
  }, [visible, docHidden]);

  // Until we know the anchor offset, render nothing rather than
  // flashing at top:0.
  if (topPx === null) return null;

  const sideStyle =
    spec.side === "left"
      ? { left: 0 }
      : spec.side === "right"
        ? { right: 0 }
        : { left: 0, right: 0 };

  const widthClass = spec.side === "full" ? "w-full" : "";
  const aspectClass =
    spec.side === "full"
      ? ""
      : spec.aspect === "16/9"
        ? "aspect-video"
        : "aspect-[9/16]";

  return (
    <div
      ref={wrapperRef}
      aria-hidden
      data-testid={`cameo-${spec.id}`}
      className={`pointer-events-none absolute ${widthClass} ${aspectClass} ${spec.extraClassName ?? ""}`}
      style={{
        top: topPx,
        height: `${effectiveHeightVh}vh`,
        zIndex: spec.layer === "front" ? 20 : 5,
        // CRITICAL: blend mode + filter MUST live on the wrapper, not
        // the inner <video>. The wrapper is `position: absolute` with
        // an explicit `z-index`, which makes it the root of its own
        // stacking context. CSS mix-blend-mode applied to a child
        // composites the child against the *parent* stacking context's
        // backdrop — and that backdrop, from the video's perspective,
        // is just the empty wrapper. Result: the blend mode silently
        // no-ops and we see the raw black plate (this was the visible
        // black rectangle bug). Hoisting blend+filter to the wrapper
        // means the wrapper-as-a-whole (with the video inside it) is
        // blended against the marketing page beneath, which is what we
        // actually want.
        //
        // `lighten` keeps per-channel max(source, page). With the
        // source's true black at ~0 and the page's dark cranberry at
        // ~(78,14,23), the black plate disappears and only the lit
        // character pixels (which exceed page values) survive.
        //
        // Filter: `brightness < 1` floors any residual H.264 shadow
        // noise (encoded "black" can sit at ~20/255) below the page's
        // weakest channel so it never bleeds through as a desaturated
        // box. Contrast keeps the lit character punchy after the
        // brightness cut; saturate preserves warm gold/cranberry tones.
        mixBlendMode: "lighten",
        filter: "brightness(0.7) contrast(1.45) saturate(1.15)",
        ...sideStyle,
      }}
    >
      <video
        ref={videoRef}
        src={spec.src}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="h-full w-full object-contain"
        style={{
          // Soft edge fade so the character (especially the full-width
          // sleigh sweep) doesn't visibly pop in or clip at the box
          // edges — they ease in and out of frame instead. The mask
          // MUST live here (on the video), not on the wrapper. Some
          // compositing paths (Chromium in particular) skip mask-image
          // on an element that also has mix-blend-mode, producing the
          // hard-cut "no fade" we kept seeing. Splitting them — blend
          // stays on the wrapper, mask lives on the video child — gets
          // a proper soft fade in every browser.
          //
          // Full-width sweeps fade both horizontal edges. Side-anchored
          // cameos fade the inward edge so they don't hard-clip into
          // the page body. Vertical edges also fade slightly so the
          // top/bottom of the box never shows a sharp horizon line.
          WebkitMaskImage:
            spec.side === "full"
              ? "linear-gradient(to right, transparent 0%, black 22%, black 78%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)"
              : spec.side === "left"
                ? "linear-gradient(to right, black 0%, black 60%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)"
                : "linear-gradient(to left, black 0%, black 60%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)",
          WebkitMaskComposite: "source-in",
          maskComposite: "intersect",
          maskImage:
            spec.side === "full"
              ? "linear-gradient(to right, transparent 0%, black 22%, black 78%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)"
              : spec.side === "left"
                ? "linear-gradient(to right, black 0%, black 60%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)"
                : "linear-gradient(to left, black 0%, black 60%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)",
        }}
      />
    </div>
  );
}

export function MarketingCharacterCameos() {
  const isMobile = useIsMobile();
  const reduced = usePrefersReducedMotion();
  // Decorative only — skip entirely when motion is reduced. The
  // cameos are atmospheric; nothing functional depends on them.
  if (reduced) return null;
  const visible = CAMEOS.filter((c) => (isMobile ? c.mobile : true));
  return (
    <>
      {visible.map((c) => (
        <CameoVideo key={c.id} spec={c} />
      ))}
    </>
  );
}
